import React, { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import api from '../../services/api';
import type { OneTimeExpenseRow } from '../../types';
import { formatMoney } from '../commercial/contact';
import { todayIso } from './ExpenseDialog';

interface Props {
  expense: OneTimeExpenseRow | null;
  onClose: () => void;
  onPaid: (e: OneTimeExpenseRow) => void;
}

/** Pago total o parcial de una deuda cargada como gasto. */
const PayExpenseDialog: React.FC<Props> = ({ expense, onClose, onPaid }) => {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(todayIso());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!expense) return;
    setAmount(String(expense.balance));
    setDate(todayIso());
    setError(null);
  }, [expense]);

  const pay = async () => {
    if (!expense) return;
    const n = Number(amount.replace(',', '.')) || 0;
    if (n <= 0) { setError('El monto tiene que ser mayor a cero.'); return; }
    setSaving(true);
    setError(null);
    try {
      onPaid(await api.payOneTimeExpense(expense.id, { amount: n, date }));
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo registrar el pago.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!expense} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Registrar pago</DialogTitle>
      <DialogContent>
        {expense && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {expense.description}{expense.supplierName ? ` · ${expense.supplierName}` : ''}<br />
              Total {formatMoney(expense.amount)} · pagado {formatMoney(expense.paidAmount)} · <b>debe {formatMoney(expense.balance)}</b>
            </Typography>
            <TextField label="Monto que pagás" value={amount} onChange={e => setAmount(e.target.value)} inputProps={{ inputMode: 'decimal' }} autoFocus />
            <TextField label="Fecha del pago" type="date" value={date} onChange={e => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={pay} disabled={saving}>{saving ? 'Guardando…' : 'Registrar pago'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PayExpenseDialog;
